import { AxiosError } from 'axios';
import { 
  ChartCategoryTypes, 
  Transcript, 
  SubjectArea, 
  DemographicChartLabels, 
  SelectedTranscriptsIdsByCategory,
  TranscripsCategoriesKeys,
  CategoriesWithValues,
  CategorizedSelectedTranscriptsIds,
  SelectedTranscriptsSummary
} from '../../interfaces/TranscriptsInterface';
import { UserDetails } from '../../interfaces/UserDetailsInterface';
import { saveSingleSelection, unsaveSingleSelection } from '../../services/transcriptsService';
import { DemographicsSummary } from '../../interfaces/DataQcInterface';
import { TRANSCRIPTS_CATEGORIES } from '../../constants';

const chartCategories = [
  ChartCategoryTypes.RACE,
  ChartCategoryTypes.GENDER,
  ChartCategoryTypes.STUDENT_WITH_DISABILITIES,
  ChartCategoryTypes.ENGLISH_LEARNER,
  ChartCategoryTypes.LOW_INCOME
];

type categoriesWithValues = { [key in ChartCategoryTypes]: { [key: string]: number } };

export const getCategoryCountsByType = (data: Transcript[]) => {
  const categoryCountsByType: categoriesWithValues = {
    [ChartCategoryTypes.RACE]: {},
    [ChartCategoryTypes.GENDER]: {},
    [ChartCategoryTypes.STUDENT_WITH_DISABILITIES]: {},
    [ChartCategoryTypes.ENGLISH_LEARNER]: {},
    [ChartCategoryTypes.LOW_INCOME]: {}
  };

  data.forEach(dataItem => {
    chartCategories.forEach((chartCategory) => {
      let currentCategoryData = categoryCountsByType[chartCategory];
      let subCategoryValue = dataItem[chartCategory];

      if (currentCategoryData.hasOwnProperty(subCategoryValue)) {
        currentCategoryData[subCategoryValue]++;
      } else {
        currentCategoryData[subCategoryValue] = 1;
      }
    });
  });

  return categoryCountsByType;
};

export const calcSelectedTranscriptsDemographicPercentages = (demographicsSummary: DemographicsSummary | undefined, selectedTranscripts: Transcript[]) => {
  // display the percentage of the current selection
  const selectedTranscriptsDemographicPercentages: categoriesWithValues = {
    [ChartCategoryTypes.RACE]: {},
    [ChartCategoryTypes.GENDER]: {},
    [ChartCategoryTypes.STUDENT_WITH_DISABILITIES]: {},
    [ChartCategoryTypes.ENGLISH_LEARNER]: {},
    [ChartCategoryTypes.LOW_INCOME]: {}
  };

  if (!demographicsSummary) return selectedTranscriptsDemographicPercentages;

  const selectedCount = getCategoryCountsByType(selectedTranscripts);
  const ignoreSubcategories = ['WITHOUT DISABILITIES', 'NATIVE / FLUENT', 'ABOVE LOW-INCOME', 'No'];

  let totalDemographicsPercentages = demographicsSummary.total.demographic_percentages;
  for (const categoryType in totalDemographicsPercentages) {
    const subCategoriesObject = totalDemographicsPercentages[categoryType as ChartCategoryTypes];
    const selectedSubCategoriesObject = selectedCount[categoryType as ChartCategoryTypes] || {};

    for (const key in subCategoriesObject) {
      if (!ignoreSubcategories.includes(key)) {
        if (selectedTranscripts.length === 0) selectedTranscriptsDemographicPercentages[categoryType as ChartCategoryTypes][key] = 0;
        else {
          const selectedSubCategoryCount = selectedSubCategoriesObject[key] || 0;
  
          let formattedPercentage = Number(((selectedSubCategoryCount / selectedTranscripts.length) * 100).toFixed(1));
          selectedTranscriptsDemographicPercentages[categoryType as ChartCategoryTypes][key] = formattedPercentage;
        }
      }
    }
  }

  return selectedTranscriptsDemographicPercentages;
};

type DemographicCategoriesData = DemographicsSummary['total']['demographic_percentages'];
type SubCategoriesKeys = typeof ChartCategoryTypes['LOW_INCOME'] | typeof ChartCategoryTypes['ENGLISH_LEARNER'] | typeof ChartCategoryTypes['STUDENT_WITH_DISABILITIES'];
type SubCategoriesForChart = { [key in SubCategoriesKeys]: string };

export const prepareDemographicData = (data: DemographicCategoriesData) => {
  const subCategoriesForChart: SubCategoriesForChart = {
    [ChartCategoryTypes.LOW_INCOME]: 'LOW-INCOME',
    [ChartCategoryTypes.ENGLISH_LEARNER]: 'ENGLISH LANGUAGE LEARNER',
    [ChartCategoryTypes.STUDENT_WITH_DISABILITIES]: 'WITH DISABILITIES'
  };

  const newData = JSON.parse(JSON.stringify(data));

  (Object.keys(subCategoriesForChart) as SubCategoriesKeys[]).forEach(category => {
    let subCategory = subCategoriesForChart[category];
    newData[category] = { [subCategory]: newData[category]['Yes'] || 0 };
  });

  return newData;
};

export const extractChartData = (categoryData: { [key: string]: number }) => {
  const labels: string[] = [];
  const data: number[] = [];

  Object.entries(categoryData).forEach(([key, value]) => {
    const label = DemographicChartLabels[key as keyof typeof DemographicChartLabels] || key;
    labels.push(label);
    data.push(value);
  });

  return { labels, data };
};

export const buildChartData = (data: DemographicCategoriesData | undefined, chartCategories:  ChartCategoryTypes[], barColor: string) => {
  let combinedLabels: string[] = [];
  let combinedData: number[] = [];

  if (!data) {
    return { labels: [], dataSet: { data: [], backgroundColor: barColor } };
  }

  // console.log(data, chartCategories);
  chartCategories.forEach(chartCategory => {
    let { labels, data: extractedData } = extractChartData(data[chartCategory]);
    combinedLabels = combinedLabels.concat(labels);
    combinedData = combinedData.concat(extractedData);
  });

  return { labels: combinedLabels, dataSet: { data: combinedData, backgroundColor: barColor } };
};

export const createChartConfigs = (data: DemographicCategoriesData, barColor: string) => {
  // console.log(data);
  let raceChartData = buildChartData(data, [ChartCategoryTypes.RACE], barColor);
  let genderChartData = buildChartData(data, [ChartCategoryTypes.GENDER], barColor);
  let miscChartData = buildChartData(
    data,
    [
      ChartCategoryTypes.STUDENT_WITH_DISABILITIES,
      ChartCategoryTypes.ENGLISH_LEARNER,
      ChartCategoryTypes.LOW_INCOME
    ],
    barColor
  );

  let raceChartConfig = {
    title: 'Race/Ethnicity',
    labels: raceChartData.labels,
    dataSet: raceChartData.dataSet
  };

  let genderChartConfig = {
    title: 'Gender',
    labels: genderChartData.labels,
    dataSet: genderChartData.dataSet
  };

  let miscChartConfig = {
    title: 'Additional demographics',
    labels: miscChartData.labels,
    dataSet: miscChartData.dataSet
  };

  return [raceChartConfig, genderChartConfig, miscChartConfig];
};

export const formatHelperTextCount = (count: number, itemType: string) => {return `${count} ${count === 1 ? itemType : `${itemType}s`}`;};

const sortTableHeaderSubjectAreas = (tableHeaderData: { key: string; label: string, subLabel?: string }[]) => {
  const groupedPairs = [];

  for (let i = 0; i < tableHeaderData.length; i++) {
    const current = tableHeaderData[i];
    const next = tableHeaderData[i + 1];
  
    if (current.subLabel && next && !next.subLabel) {
      groupedPairs.push([current, next]);
      i++;
    }
  }
  
  groupedPairs.sort((a, b) => a[0].label.localeCompare(b[0].label));
  
  return groupedPairs.flat();
};

export function prepareTranscriptDetailsData(terms: any[]) {

  const tableHeaderData: { key: string; label: string, subLabel?: string }[] = [];
  
  const subjects = new Map<string, number>(); 
  let creditsPerGrade = 0;

  terms.forEach((term) => {

    term.subject_areas.forEach((subjectArea: SubjectArea) => {
        
      subjects.set(
        subjectArea.name,
        (subjects.get(subjectArea.name) || 0) + subjectArea.num_credits
      );

      creditsPerGrade +=  subjectArea.num_credits;  
    });
  });
  
  subjects.forEach((creditCount, subjectName) => {
    tableHeaderData.push({ 
      key: subjectName, 
      label: subjectName, 
      subLabel: formatHelperTextCount(creditCount, 'credit')
    });
    tableHeaderData.push({ key: `${subjectName}-grade`, label: '' });
  });

  const sortedTableHeaderData = sortTableHeaderSubjectAreas(tableHeaderData);
  
  return { tableHeaderData: sortedTableHeaderData, creditsPerGrade };
}

const addToSelectedTranscripts = (callback: (value: React.SetStateAction<SelectedTranscriptsSummary>) => void, category: string, transcript: Transcript) => {
  callback(prev => ({
    ...prev,
    selection_counts: {
      ...prev.selection_counts,
      [category]: (prev.selection_counts[category] || 0) + 1,
    },
    selectedTranscriptsByCategory: {
      ...prev.selectedTranscriptsByCategory,
      [category]: [
        ...prev.selectedTranscriptsByCategory[category],
        transcript
      ]
    }
  }));
};

const removeFromSelectedTranscripts = (callback: (value: React.SetStateAction<SelectedTranscriptsSummary>) => void, category: string, transcriptId: string) => {
  callback(prev => ({
    ...prev,
    selection_counts: {
      ...prev.selection_counts,
      [category]: Math.max((prev.selection_counts[category] || 0) - 1, 0),
    },
    selectedTranscriptsByCategory: {
      ...prev.selectedTranscriptsByCategory,
      [category]: prev.selectedTranscriptsByCategory[category].filter(selectedTranscript => selectedTranscript.id !== transcriptId) || []
    }
  }));
};

type OnErrorCallBack = (errorMessage: string, error: Error | AxiosError) => void;

export const selectTranscript = async (
  userDetails: UserDetails | undefined,
  category: TranscripsCategoriesKeys, 
  transcript: Transcript, 
  isSelected: boolean,
  schoolId: string,
  onError: OnErrorCallBack,
  onUpdateSelectedTranscriptsSummary: (value: React.SetStateAction<SelectedTranscriptsSummary>) => void
) => {
  if (!userDetails) {
    let error = new Error('Missing user details!');
    onError('Missing user details!', error as AxiosError);

    return;
  }

  const payload = {category: category, transcript_id: transcript.id};

  if (isSelected) {
    try {
      removeFromSelectedTranscripts(onUpdateSelectedTranscriptsSummary, category, transcript.id);
      await unsaveSingleSelection(userDetails.eoaId, userDetails.access_token, schoolId, payload);
    } catch (error) {
      console.log(error);
      onError('Error occured while unsaving a selected transcript!', error as AxiosError);
      addToSelectedTranscripts(onUpdateSelectedTranscriptsSummary, category, transcript);
    }
    return;
  }

  try {
    addToSelectedTranscripts(onUpdateSelectedTranscriptsSummary, category, transcript);
    await saveSingleSelection(userDetails.eoaId, userDetails.access_token, schoolId, payload);
  } catch (error) {
    console.log(error);
    onError('Error occured while saving a selected transcript!', error as AxiosError);
    removeFromSelectedTranscripts(onUpdateSelectedTranscriptsSummary, category, transcript.id);
  }
};

export const prepareCategorizedData = (selectedTranscriptsByCategory: {[key: string]: Transcript[]}) => {

  const selectedIdsByCategory: SelectedTranscriptsIdsByCategory = {
    [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: [],
    [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: [],
    [TRANSCRIPTS_CATEGORIES.STRUGGLING]: [],
  };

  for (const category of Object.keys(TRANSCRIPTS_CATEGORIES)) {
    selectedIdsByCategory[category as keyof typeof TRANSCRIPTS_CATEGORIES] = selectedTranscriptsByCategory[category].map(transcript => transcript.id) || [];
  }

  const categorizedData: CategorizedSelectedTranscriptsIds = {
    rigorous_academic_transcript_ids: [],
    on_the_cusp_transcript_ids: [],
    struggling_transcript_ids: []
  };

  (Object.keys(selectedIdsByCategory) as TranscripsCategoriesKeys[]).forEach((category) => {
    const key = `${category.toLowerCase()}_transcript_ids`;
    categorizedData[key as keyof typeof categorizedData] = selectedIdsByCategory[category];
  });

  return categorizedData;
};

export const storeLastNavigatedPage = (lastNavigated: CategoriesWithValues<number> | null, categoryName: string, page: number) => {
  
  if (!lastNavigated) {
    lastNavigated = {
      [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: 1,
      [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: 1,
      [TRANSCRIPTS_CATEGORIES.STRUGGLING]: 1,
    };
  }

  return {
    ...lastNavigated,
    [categoryName]: page,
  };
};

//delete if not needed after 697 is finished
// export const extractLastPage = (category: string, linkHeader: string) => {
//   const match = linkHeader.match(new RegExp(`page=(\\d+)>; rel="last".*?${category}`, 'i'));
//   return match ? Number(match[1] + 1) : 0;
// };

export const checkForLink = (linkHeader: string, linkRel: string) => {
  const regex = new RegExp(`rel="${linkRel}"`, 'i');
  return regex.test(linkHeader);
};

export const checkForNextPage = (linkHeader: string): boolean => {
  const regex = /<[^>]+\?page=\d+>; rel="next"/i;
  return regex.test(linkHeader);
};